// src/components/common/cards/KPICard/KPICard.styles.js
import { styled } from '@mui/material/styles';
import { Card, Box, Chip } from '@mui/material';

// Default theme colors for KPI cards
export const defaultKPITheme = {
  primary: '#2196f3',
  secondary: '#ff9800', 
  success: '#4caf50',
  error: '#f44336',
  warning: '#ff9800',
  info: '#2196f3',
  grey: '#9e9e9e'
};

export const CardContentContainer = styled(Box)(({ theme, compact }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(compact ? 1.5 : 2),
  height: '100%',
  padding: theme.spacing(compact ? 1.5 : 3),
}));

export const TextContainer = styled(Box)(({ theme, compact }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  flexGrow: 1,
  textAlign: 'center',
  gap: theme.spacing(compact ? 0.25 : 0.5),
}));

// Styled components (for advanced usage)
export const StyledKPICard = styled(Card)(({ theme, compact }) => ({
  height: compact ? 'auto' : '100%',
  minHeight: compact ? '80px' : '120px',
  maxHeight: compact ? '80px' : 'none',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  borderRadius: theme.spacing(compact ? 1.5 : 2),
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
  },
}));

export const IconContainer = styled(Box)(({ theme, iconcolor, compact }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: compact ? 36 : 48,
  height: compact ? 36 : 48,
  borderRadius: '50%',
  backgroundColor: iconcolor || theme.palette.primary.main,
  color: 'white',
  marginBottom: theme.spacing(compact ? 1 : 2),
  flexShrink: 0,
}));

export const ValueContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

export const VarianceChip = styled(Chip)(({ theme, variant }) => ({
  fontSize: '0.75rem',
  height: 24,
  borderRadius: 12,
  backgroundColor: variant === 'positive' 
    ? theme.palette.success.light 
    : variant === 'negative' 
    ? theme.palette.error.light 
    : theme.palette.grey[200],
  color: variant === 'positive' 
    ? theme.palette.success.dark 
    : variant === 'negative' 
    ? theme.palette.error.dark 
    : theme.palette.text.secondary,
  '& .MuiChip-label': {
    fontWeight: 600,
  },
}));

export const RatingChip = styled(Chip)(({ theme, ratingcolor, ratingbg, compact }) => ({
  fontSize: compact ? '0.65rem' : '0.75rem',
  height: compact ? 20 : 24,
  borderRadius: compact ? 10 : 12,
  backgroundColor: ratingbg || theme.palette.grey[200],
  color: ratingcolor || theme.palette.text.secondary,
  '& .MuiChip-label': {
    fontWeight: 600,
  },
}));

// Utility functions for formatting values
export const formatCurrency = (value, options = {}) => {
  const defaultOptions = {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  };
  
  return new Intl.NumberFormat('en-US', { ...defaultOptions, ...options }).format(value);
};

export const formatNumber = (value, options = {}) => {
  return new Intl.NumberFormat('en-US', options).format(value);
};

export const formatPercentage = (value, decimals = 1) => {
  return `${value > 0 ? '+' : ''}${value.toFixed(decimals)}%`;
};

// Legacy variance calculation (for backwards compatibility)
export const calculateVariance = (actual, projected) => {
  if (!projected || projected === 0) return null;
  
  const difference = actual - projected;
  const percentage = (difference / projected) * 100;
  
  return {
    value: difference,
    percentage: formatPercentage(percentage),
    type: difference > 0 ? 'positive' : difference < 0 ? 'negative' : 'neutral'
  };
};

// Utility function to get rating for a value (can be used externally)
export const getRatingForValue = (value, ratingConfig) => {
  if (!ratingConfig?.ranges) return null;
  
  const numericValue = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(numericValue)) return null;
  
  const range = ratingConfig.ranges.find(r => 
    numericValue >= r.min && numericValue <= r.max
  );
  
  return range || null;
};

// Utility function to get icon color based on rating
export const getIconColorFromRating = (value, ratingConfig, defaultColor) => {
  const rating = getRatingForValue(value, ratingConfig);
  return rating?.color || defaultColor;
};

// Common KPI configurations that can be used as defaults
export const commonKPITypes = {
  revenue: {
    type: 'currency',
    iconType: 'money',
    iconColor: defaultKPITheme.success
  },
  sales: {
    type: 'currency', 
    iconType: 'trending',
    iconColor: defaultKPITheme.primary
  },
  performance: {
    type: 'percentage',
    iconType: 'check',
    iconColor: defaultKPITheme.success
  },
  efficiency: {
    type: 'percentage',
    iconType: 'analytics',
    iconColor: defaultKPITheme.info
  },
  labor: {
    type: 'currency',
    iconType: 'person',
    iconColor: defaultKPITheme.warning
  },
  time: {
    type: 'number',
    iconType: 'timer',
    iconColor: defaultKPITheme.secondary
  }
};

// Common rating configurations
export const commonRatingConfigs = {
  performance: {
    ranges: [
      { min: 0,  max: 49, color: '#ffffff', bgColor: '#f44336', label: 'Poor' },
      { min: 50, max: 74, color: '#000000', bgColor: '#ff9800', label: 'Fair' },
      { min: 75, max: 89, color: '#ffffff', bgColor: '#4caf50', label: 'Good' },
      { min: 90, max: 100, color: '#ffffff', bgColor: '#2196f3', label: 'Excellent' }
    ]
  },
  variance: {
    ranges: [
      { min: -100, max: -5, color: '#ffffff', bgColor: '#f44336', label: 'Behind' },
      { min: -5,   max: 5,  color: '#000000', bgColor: '#ff9800', label: 'On Track' },
      { min: 5,    max: 100, color: '#ffffff', bgColor: '#4caf50', label: 'Ahead' }
    ]
  },
  priority: {
    ranges: [
      { min: 0,    max: 1000,  color: '#ffffff', bgColor: '#4caf50', label: 'Low' },
      { min: 1001, max: 5000,  color: '#000000', bgColor: '#ff9800', label: 'Medium' },
      { min: 5001, max: 99999, color: '#ffffff', bgColor: '#f44336', label: 'High' }
    ]
  }
};